import React, { useState, useCallback, useMemo } from 'react';
import { Box } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCalendarViewStore } from './store/calendarViewStore';
import { useScheduleItems, useCreateScheduleItem, useUpdateScheduleItem, useDeleteScheduleItem } from './hooks/useScheduleItems';
import { WorkOrderFormData } from './components/ScheduleItemDialog';
import ScheduleItemAPI from './api/scheduleItemApi';
import {
  buildSegmentsFromCreateResponse,
  resolveSegmentIdsForUpdate,
} from './utils/workOrderSegmentSync';
import { 
  ScheduleItem, 
  CreateScheduleItemRequest, 
  UpdateScheduleItemRequest,
  CreateWorkOrderFromScheduleRequest,
  UpdateWorkOrderFromScheduleRequest,
  SegmentUpdateRequest,
} from './types/scheduleItem.types';
import { getAuditUserName } from '../common/Utils/auditUser';
import { CalendarToolbar } from './components/CalendarToolbar';
import { ScheduleItemDialog } from './components/ScheduleItemDialog';
import { DayView } from './views/DayView';
import { WeekView } from './views/WeekView';
import { WorkWeekView } from './views/WorkWeekView';
import { MonthView } from './views/MonthView';
import { getDateRangeForView, formatDate, toLocalISOString } from './utils/timeUtils';
import { normalizeScheduleItemsFilter } from './utils/woDateFilter';
import WorkOrderDialogContainerMain from '../workOrder/WorkOrderDialogContainerMain';

interface IntakeCalendarContainerProps {
  currentUserId: number;
  locations: { locationId: number; locationName: string }[];
  selectedLocationId?: number;
}

export function IntakeCalendarContainer({
  currentUserId,
  locations,
  selectedLocationId,
}: IntakeCalendarContainerProps) {
  const { enqueueSnackbar } = useSnackbar();

  // Store state
  const {
    viewType,
    dayViewDays,
    currentDate,
    timeScale,
    setViewType,
    setDayViewDays,
    setTimeScale,
    setCurrentDate,
    goToToday,
    goToPrevious,
    goToNext,
  } = useCalendarViewStore();

  // Dialog state
  const [sidePanelOpen, setSidePanelOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<ScheduleItem | null>(null);
  const [createDate, setCreateDate] = useState<Date | undefined>();
  const [createTime, setCreateTime] = useState<Date | undefined>();

  // Work Order Dialog state (for opening work orders from card)
  const [workOrderDialogOpen, setWorkOrderDialogOpen] = useState(false);
  const [selectedInvoiceDocId, setSelectedInvoiceDocId] = useState<number | null>(null);

  // Get date range for API query
  const { start, end } = useMemo(() => 
    getDateRangeForView(currentDate, viewType, dayViewDays),
    [currentDate, viewType, dayViewDays]
  );

  // Fetch schedule items using local ISO strings to match database storage format.
  const filter = useMemo(
    () =>
      normalizeScheduleItemsFilter({
        startDate: toLocalISOString(start),
        endDate: toLocalISOString(end),
        locationId: selectedLocationId,
      }),
    [selectedLocationId, start, end],
  );
  
  const { data: scheduleItems = [], isLoading, refetch, isFetching, status } = useScheduleItems(filter);

  const visibleScheduleItems = scheduleItems;

  // Mutations
  const createMutation = useCreateScheduleItem();
  const updateMutation = useUpdateScheduleItem();
  const deleteMutation = useDeleteScheduleItem();
  const queryClient = useQueryClient();
  
  // Work order creation mutation for intake calendar
  const createWorkOrderMutation = useMutation({
    mutationFn: ScheduleItemAPI.createWorkOrderFromSchedule,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduleItems'] });
    },
  });

  // Work order update mutation pending state (for disabling buttons)
  const [isWorkOrderUpdatePending, setIsWorkOrderUpdatePending] = useState(false);

  // Combined pending state: either creating or updating a work order
  const isWorkOrderOperationPending = createWorkOrderMutation.isPending || isWorkOrderUpdatePending;

  // Date range label
  const dateRangeLabel = useMemo(() => {
    if (viewType === 'month') {
      return currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    }
    if (viewType === 'day' && dayViewDays === 1) {
      return formatDate(currentDate, 'long');
    }
    return `${formatDate(start, 'short')} - ${formatDate(end, 'short')}`;
  }, [viewType, dayViewDays, currentDate, start, end]);

  // Handle item click
  const handleItemClick = useCallback((item: ScheduleItem) => {
    setSelectedItem(item);
    setCreateDate(undefined);
    setCreateTime(undefined);
    setSidePanelOpen(true);
  }, []);

  const handleOpenWorkOrder = useCallback((invoiceDocId: number) => {
    setSelectedInvoiceDocId(invoiceDocId);
    setWorkOrderDialogOpen(true);
  }, []);

  // Handle close work order dialog
  const handleCloseWorkOrderDialog = useCallback(() => {
    setWorkOrderDialogOpen(false);
    setSelectedInvoiceDocId(null);
    // Refetch schedule items to reflect any changes made in work order
    refetch();
  }, [refetch]);

  // Handle create click on empty slot
  const handleCreateClick = useCallback((date: Date, time: Date) => {
    setSelectedItem(null);
    setCreateDate(date);
    setCreateTime(time);
    setSidePanelOpen(true);
  }, []);

  // Handle drag-drop reschedule (or create for virtual/unscheduled items)
  const handleDrop = useCallback(async (e: React.DragEvent, date: Date, time: Date) => {
    const data = e.dataTransfer.getData('application/json');
    if (!data) return;

    try {
      const dragData = JSON.parse(data);
      if (dragData.type !== 'schedule-item') return;

      const newStart = new Date(date);
      newStart.setHours(time.getHours(), time.getMinutes(), 0, 0);

      // Virtual item (no scheduleItemId) — create a new ScheduleItem for this WO
      if (!dragData.scheduleItemId && dragData.workOrderId) {
        return; // WO-linked items cannot be moved via drag-and-drop
      }

      // Existing schedule item — update position (WO-linked items cannot be moved)
      if (dragData.scheduleItemId) {
        const item = visibleScheduleItems.find(i => i.scheduleItemId === dragData.scheduleItemId);
        if (!item) return;
        if (item.workOrderId) return;

        const durationMs = item.endDateTime.getTime() - item.startDateTime.getTime();
        const newEnd = new Date(newStart.getTime() + durationMs);

        const updateRequest: UpdateScheduleItemRequest = {
          scheduleItemId: item.scheduleItemId,
          customerId: item.customerId,
          locationId: item.locationId,
          startDateTime: toLocalISOString(newStart),
          endDateTime: toLocalISOString(newEnd),
          title: item.title,
          description: item.description || '',
          status: item.status,
          timeZoneId: item.timeZoneId,
          modifiedBy: currentUserId,
          rowVersion: item.rowVersion,
        };

        updateMutation.mutate(updateRequest, {
          onSuccess: () => {
            enqueueSnackbar("Schedule item rescheduled successfully", { variant: "success" });
            refetch();
          },
          onError: (error: any) => {
            const errorMessage = error?.response?.data?.message || error?.message || "Failed to reschedule item";
            enqueueSnackbar(errorMessage, { variant: "error" });
            refetch();
          },
        });
      }
    } catch (error: any) {
      const errorMessage = error?.message || "Failed to reschedule item";
      enqueueSnackbar(errorMessage, { variant: "error" });
      refetch();
    }
  }, [visibleScheduleItems, selectedLocationId, currentUserId, createMutation, updateMutation, refetch, enqueueSnackbar]);

  // Handle save from dialog
  const handleSave = useCallback(async (request: CreateScheduleItemRequest | UpdateScheduleItemRequest) => {
    if ('scheduleItemId' in request) {
      // Update
      await updateMutation.mutateAsync(request);
      await refetch();
      setSidePanelOpen(false);
      setSelectedItem(null);
    } else {
      // Create
      await createMutation.mutateAsync(request);
      await refetch();
      setSidePanelOpen(false);
      setSelectedItem(null);
    }
  }, [createMutation, updateMutation, refetch]);

  // Handle delete
  const handleDelete = useCallback((scheduleItemId: number) => {
    deleteMutation.mutate(scheduleItemId, {
      onSuccess: () => {
        enqueueSnackbar("Intake Schedule removed successfully", { variant: "success" });
        setSidePanelOpen(false);
        setSelectedItem(null);
      },
      onError: (error: any) => {
        const errorMessage = error?.response?.data?.message || error?.message || "Failed to delete schedule item";
        enqueueSnackbar(errorMessage, { variant: "error" });
      },
    });
  }, [deleteMutation, enqueueSnackbar]);

  // Handle month view date click
  const handleMonthDateClick = useCallback((date: Date) => {
    // Switch to day view for the clicked date
    setViewType('day');
    useCalendarViewStore.getState().setCurrentDate(date);
  }, [setViewType]);

  // Handle create/update/archive work order from dialog
  const handleCreateWorkOrder = useCallback(async (workOrderData: WorkOrderFormData) => {
    const userName = getAuditUserName();
    const dtNow = new Date().toISOString();
    const actionType = workOrderData.actionType || 'commit';

    // Handle archive action
    if (actionType === 'archive') {
      // TODO: Implement archive API call when available
      enqueueSnackbar("Archive functionality coming soon", { variant: "info" });
      return;
    }

    // Use workOrderData.workOrderId first; fall back to selectedItem?.workOrderId
    // to guard against edge cases where the prop update hasn't propagated yet.
    const existingWorkOrderId = workOrderData.workOrderId || selectedItem?.workOrderId;

    // Update existing work order (save, commit draft, or re-save draft)
    if (
      existingWorkOrderId &&
      (actionType === 'save' || actionType === 'commit' || actionType === 'draft')
    ) {
      const segmentsWithIds = resolveSegmentIdsForUpdate(
        workOrderData.segments ?? [],
        selectedItem,
      );

      const segmentsUpdate: SegmentUpdateRequest[] = segmentsWithIds
        .filter(
          (seg) =>
            seg.segmentTitle?.trim().length > 0 &&
            seg.serviceCodeIdentity > 0,
        )
        .map((seg) => ({
          segmentId: seg.segmentId ?? 0,
          displayText: seg.segmentTitle,
          serviceCodeIdentity: seg.serviceCodeIdentity,
          billAs: seg.billAs,
          estimatedHours: seg.estimatedHours,
        }));

      const updateRequest: UpdateWorkOrderFromScheduleRequest = {
        workOrderId: existingWorkOrderId,
        segmentId: selectedItem?.segmentId,
        customerIdentity: workOrderData.customerIdentity,
        locationIdentity: workOrderData.locationIdentity,
        unitIdentity: workOrderData.unitIdentity,
        woStatusIdentity: workOrderData.woStatusIdentity,
        woPriorityIdentity: workOrderData.woPriorityIdentity,
        salesPersonIdentity: workOrderData.salesPersonIdentity,
        technicianIdentity: workOrderData.technicianIdentity,
        segmentTitle: workOrderData.segmentTitle,
        serviceCodeIdentity: workOrderData.serviceCodeIdentity,
        billAs: workOrderData.segments?.[0]?.billAs,
        estimatedHours: workOrderData.segments?.[0]?.estimatedHours,
        segments: segmentsUpdate,
        actionType: actionType === 'commit' ? 'commit' : 'save',
        modifiedBy: userName,
      };

      setIsWorkOrderUpdatePending(true);
      try {
        const response = await ScheduleItemAPI.updateWorkOrderFromSchedule(updateRequest);
        const defaultMessage =
          actionType === 'commit'
            ? 'Work order committed successfully'
            : 'Work order updated successfully';
        enqueueSnackbar(response.message || defaultMessage, { variant: "success" });

        const refreshResult = await refetch();
        const scheduleItemId = selectedItem?.scheduleItemId ?? workOrderData.scheduleItemId;
        const refreshed = scheduleItemId
          ? refreshResult.data?.find((i) => i.scheduleItemId === scheduleItemId)
          : undefined;

        if (refreshed) {
          setSelectedItem(refreshed);
        } else if (selectedItem) {
          setSelectedItem({
            ...selectedItem,
            workOrderId: existingWorkOrderId,
            workOrderNumber:
              workOrderData.workOrderNumber || selectedItem.workOrderNumber,
            woStatus:
              actionType === 'commit' ? 'Committed' : selectedItem.woStatus,
            woStatusId: workOrderData.woStatusIdentity,
            woPriorityId: workOrderData.woPriorityIdentity,
            woSalesPersonId: workOrderData.salesPersonIdentity,
            woTechnicianId: workOrderData.technicianIdentity,
            serviceCodeId: workOrderData.serviceCodeIdentity,
            segmentTitle: workOrderData.segmentTitle,
            segments: buildSegmentsFromCreateResponse(
              segmentsWithIds,
              undefined,
              selectedItem.segmentId,
            ),
          });
        }
      } catch (error: any) {
        const errorMessage =
          error?.response?.data?.message ||
          error?.message ||
          (actionType === 'commit'
            ? "Failed to commit work order"
            : "Failed to update work order");
        enqueueSnackbar(errorMessage, { variant: "error" });
      } finally {
        setIsWorkOrderUpdatePending(false);
      }
      return;
    }

    // Validate scheduleItemId is present
    if (!workOrderData.scheduleItemId) {
      enqueueSnackbar("Cannot create work order: Schedule item ID is missing", { variant: "error" });
      return;
    }

    // Map action type to status for new work orders
    const status: 'draft' | 'committed' = actionType === 'draft' ? 'draft' : 'committed';

    // Build segments array for multi-segment support.
    // Filter out blank placeholder rows (no title or no service code) so the
    // backend doesn't roll back the transaction on an invalid default segment.
    const segments = (workOrderData.segments ?? [])
      .filter(
        (seg) =>
          seg.segmentTitle?.trim().length > 0 &&
          seg.serviceCodeIdentity > 0,
      )
      .map((seg) => ({
        displayText: seg.segmentTitle,
        serviceCodeIdentity: seg.serviceCodeIdentity,
        billAs: seg.billAs,
        estimatedHours: seg.estimatedHours,
      }));

    const request: CreateWorkOrderFromScheduleRequest = {
      scheduleItemId: workOrderData.scheduleItemId!,
      customerIdentity: workOrderData.customerIdentity,
      locationIdentity: workOrderData.locationIdentity,
      unitIdentity: workOrderData.unitIdentity,
      woStatusIdentity: workOrderData.woStatusIdentity,
      woPriorityIdentity: workOrderData.woPriorityIdentity,
      salesPersonIdentity: workOrderData.salesPersonIdentity,
      technicianIdentity: workOrderData.technicianIdentity,
      status: status,
      actionType: actionType === 'draft' ? 'draft' : 'commit',
      // Use segments array if available, otherwise fall back to single segment
      segments: segments.length > 0 ? segments : undefined,
      segmentTitle: workOrderData.segmentTitle,
      serviceCodeIdentity: workOrderData.serviceCodeIdentity,
      // Add schedule info for technician calendar if technician is assigned
      schedule: workOrderData.technicianIdentity ? {
        technicianId: workOrderData.technicianIdentity,
        workDate: selectedItem?.startDateTime ? new Date(selectedItem.startDateTime).toISOString() : dtNow,
        estimatedHours: segments.reduce((sum, seg) => sum + seg.estimatedHours, 0) || 1,
        overCapacity: false,
        technicianOff: false,
      } : undefined,
      createdBy: userName,
      createdDate: dtNow,
      modifiedBy: userName,
      modifiedDate: dtNow,
    };

    createWorkOrderMutation.mutate(request, {
      onSuccess: async (response) => {
        const message = actionType === 'draft' 
          ? `Work order ${response.documentNumber || ''} saved as draft successfully`
          : `Work order ${response.documentNumber || ''} committed successfully`;
        enqueueSnackbar(message, { variant: "success" });
        
        const refreshResult = await refetch();
        const scheduleItemId = workOrderData.scheduleItemId ?? selectedItem?.scheduleItemId;
        const refreshed = scheduleItemId
          ? refreshResult.data?.find((i) => i.scheduleItemId === scheduleItemId)
          : undefined;

        if (refreshed) {
          setSelectedItem(refreshed);
        } else if (selectedItem && response.invoiceDocumentIdentity) {
          const persistedSegments = buildSegmentsFromCreateResponse(
            workOrderData.segments ?? [],
            response.segmentResults,
            response.segmentId,
          );

          setSelectedItem({
            ...selectedItem,
            workOrderId: response.invoiceDocumentIdentity,
            workOrderNumber: response.documentNumber,
            woStatus: actionType === 'draft' ? 'Draft' : 'Committed',
            woTechnicianId: workOrderData.technicianIdentity,
            woPriorityId: workOrderData.woPriorityIdentity,
            woStatusId: workOrderData.woStatusIdentity,
            woSalesPersonId: workOrderData.salesPersonIdentity,
            serviceCodeId: workOrderData.serviceCodeIdentity,
            segmentTitle: workOrderData.segmentTitle,
            segmentId: persistedSegments[0]?.segmentId ?? response.segmentId,
            segments: persistedSegments,
          });
        }
      },
      onError: (error: any) => {
        const errorMessage = error?.response?.data?.message || error?.message || "Failed to create work order";
        enqueueSnackbar(errorMessage, { variant: "error" });
      },
    });
  }, [createWorkOrderMutation, refetch, enqueueSnackbar, selectedItem]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        bgcolor: 'background.default',
      }}
    >
      {/* Toolbar */}
      <CalendarToolbar
        viewType={viewType}
        dayViewDays={dayViewDays}
        currentDate={currentDate}
        timeScale={timeScale}
        onViewTypeChange={setViewType}
        onDayViewDaysChange={setDayViewDays}
        onTimeScaleChange={setTimeScale}
        onGoToToday={goToToday}
        onGoPrevious={goToPrevious}
        onGoNext={goToNext}
        onDateSelect={setCurrentDate}
        dateRangeLabel={dateRangeLabel}
      />

      {/* Calendar view */}
      <Box sx={{ flex: 1, overflow: 'hidden', p: 1 }}>
        {viewType === 'day' && (
          <DayView
            currentDate={currentDate}
            dayViewDays={dayViewDays}
            items={visibleScheduleItems}
            timeScale={timeScale}
            onItemClick={handleItemClick}
            onDrop={handleDrop}
            onCreateClick={handleCreateClick}
            allowDrag
            onOpenWorkOrder={handleOpenWorkOrder}
          />
        )}

        {viewType === 'week' && (
          <WeekView
            currentDate={currentDate}
            items={visibleScheduleItems}
            timeScale={timeScale}
            onItemClick={handleItemClick}
            onDrop={handleDrop}
            onCreateClick={handleCreateClick}
            allowDrag
            onOpenWorkOrder={handleOpenWorkOrder}
          />
        )}

        {viewType === 'workweek' && (
          <WorkWeekView
            currentDate={currentDate}
            items={visibleScheduleItems}
            timeScale={timeScale}
            onItemClick={handleItemClick}
            onDrop={handleDrop}
            onCreateClick={handleCreateClick}
            allowDrag
            onOpenWorkOrder={handleOpenWorkOrder}
          />
        )}

        {viewType === 'month' && (
          <MonthView
            currentDate={currentDate}
            items={visibleScheduleItems}
            onDateClick={handleMonthDateClick}
            onItemClick={handleItemClick}
          />
        )}
      </Box>

      {/* Schedule Item Dialog */}
      <ScheduleItemDialog
        open={sidePanelOpen}
        onClose={() => {
          setSidePanelOpen(false);
          setSelectedItem(null);
        }}
        item={selectedItem}
        initialDate={createDate}
        initialTime={createTime}
        locations={locations}
        currentUserId={currentUserId}
        onSave={handleSave}
        onDelete={handleDelete}
        onCreateWorkOrder={handleCreateWorkOrder}
        isWorkOrderOperationPending={isWorkOrderOperationPending}
      />

      {/* Work Order Dialog (opened from card) */}
      {selectedInvoiceDocId && (
        <WorkOrderDialogContainerMain
          open={workOrderDialogOpen}
          showHideWoPopup={(isDialogToShow) => {
            if (!isDialogToShow) {
              handleCloseWorkOrderDialog();
            }
          }}
          InvoiceDocIdentity={selectedInvoiceDocId}
        />
      )}
    </Box>
  );
}

export default IntakeCalendarContainer;
